<template> 
  <b-form @submit.stop.prevent="submit">
    <b-form-group label="Date">
      <DatePicker v-model="form.date" :disabled-dates="datepicker.disabledDates" input-class="form-control"></datepicker>
    </b-form-group>

    <b-form-group label="Songs">
      <draggable v-model="form.song_listings" class="list-group" draggable=".list-group-item:not(.disabled)">
        <div v-for="(listing, index) in form.song_listings" :key="index" class="list-group-item">
          <div>
            <h6>{{listing.song.title}}</h6>
            <p>
              <small>{{listing.song.body | limit }}&hellip;</small>
            </p>
          </div>

          <div @click="removeFormSongListing(listing)" class="btn btn-sm btn-link">
            <b-icon-x></b-icon-x>
          </div>
        </div>
        <div v-if="!form.song_listings.length" class="list-group-item disabled">
          No songs
        </div>

        <div slot="header">
          <Multiselect v-model="form.song_listings" :multiple="true" :options="songListings" track-by="song_id" :loading="songsIsLoading" @search-change="asyncFind" :internal-search="false" placeholder="Search songs">
            <template slot="tag">{{ '' }}</template>
            <template slot="option" slot-scope="{ option }">
              {{ option.song.title }}
            </template>
          </Multiselect>
        </div>
      </draggable>
    </b-form-group>

    <b-form-group label="Comments">
      <b-form-textarea v-model="form.comments"></b-form-textarea>
    </b-form-group>

    <div class="d-flex flex-row-reverse">
      <b-button type="submit" variant="primary">Save</b-button>
    </div>
  </b-form>
</template>

<script>
  import gql from 'graphql-tag'

  import DatePicker from '@sum.cumo/vue-datepicker'
  import Multiselect from 'vue-multiselect'
  import draggable from 'vuedraggable'

  const CREATE_PLAYLIST = gql`
    mutation CreatePlaylist($input: playlists_insert_input!) {
      playlist: insert_playlists_one(object: $input) {
        id
        date
        comments

        song_listings {
          song_id

          song {
            id
            title
            body
          }
        }
      }
    }
  `;

  const UPDATE_PLAYLIST_AND_REPLACE_SONG_LISTINGS = gql`
    mutation UpdatePlaylistAndReplaceSongListings($id: uuid!, $input: playlists_set_input!, $listed_songs_input: [playlists_songs_insert_input!]!) {
      delete_playlists_songs(where: {
        playlist_id: { _eq: $id }
      }) {
        affected_rows
      }
      
      insert_playlists_songs(objects: $listed_songs_input) {
        affected_rows
      }

      playlist: update_playlists_by_pk(
        pk_columns: { id: $id },
        _set: $input
      ) {
        id
        date
        comments

        song_listings {
          song_id
          playlist_id
          
          song {
            id
            title
            body
          }
        }

        created_at
        updated_at
      }
    }
  `;

  const SEARCH_SONGS = gql`
    query SearchSongs($search: String!) {
      songs: search_songs(args: { search: $search }) {
        id
        title
        body
        key
        updated_at
      }
    }
  `

  export default {
    props: ['id', 'playlist'],

    components: {
      DatePicker,
      Multiselect,
      draggable
    },

    data() {
      return {
        changed: false,
        form: this.initForm(),

        songListings: [],
        songsIsLoading: false,

        datepicker: {
          disabledDates: {
            days: [1, 2, 4, 5]
          }
        }
      }
    },

    watch: {
      form: {
        handler() {
          this.$emit('changed');
        },

        deep: true
      }
    },

    methods: {
      initForm() {
        function dateToday() {
          const date = (new Date());

          return [date.getFullYear(), (date.getMonth() + 1).toString().padStart(2, '0'), date.getDate().toString().padStart(2, '0')].join('-')
        }

        return {
          date: this.playlist && this.playlist.date || dateToday(),
          song_listings: this.playlist && this.playlist.song_listings || []
        }
      },

      asyncFind(search) {
        this.songsIsLoading = true

        this.$apollo.query({
          query: SEARCH_SONGS,
          
          variables: {
            search: `%${search}%`
          }
        }).then(({ data }) => {
          this.songListings = data.songs.map(song => { return { song_id: song.id, song }});
          this.songsIsLoading = false
        })
      },

      removeFormSongListing(listing) {
        this.form.song_listings.splice(listing, 1);
      },

      submit() {
        if (this.playlist) {
          this.update();
        } else {
          this.create();
        }
      },

      create() {
        return this.$apollo.mutate({
          mutation: CREATE_PLAYLIST,

          variables: {
            input: Object.assign({}, this.form, {
              song_listings: { data: this.form.song_listings.map(listing => {
                return { song_id: listing.song_id }
              }) }
            })
          },

          update: (cache, { data }) => {
            this.form = this.initForm();
            this.$emit('saved', data.playlist, { cache });
          }
        })
      },

      update() {
        const id = this.$props.playlist.id;
        
        return this.$apollo.mutate({
          mutation: UPDATE_PLAYLIST_AND_REPLACE_SONG_LISTINGS,

          variables: {
            id,
            input: Object.assign({}, this.form, { song_listings: undefined }),
            listed_songs_input: this.form.song_listings.map(listing => {
              return { playlist_id: id, song_id: listing.song_id }
            })
          }
        }).then(({ data }) => {
          this.$emit('saved', data.playlist, {})
        })
      }
    }
  }
</script>

<style scoped>
  @import '~@sum.cumo/vue-datepicker/dist/Datepicker.css';
  @import '~vue-multiselect/dist/vue-multiselect.min.css';

  .list-group-item {
    display: flex;
    align-items: center;

    cursor: move;
  }

  .list-group-item h6 {
    margin-bottom: 0;
  }

  .list-group-item .btn {
    margin-left: auto;
  }
</style>